import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AssetContext } from '../contexts/AssetListContext.js';
import backIcon from '../assets/icons/navigate_before.svg';
import PdfViewerModal from '../components/PdfViewerModal.js';
import filterIcon from '../assets/icons/down_arrow.svg';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Toggle this to `true` to enable persisting column/filter state in browser localStorage
const USE_LOCAL_STORAGE = false;

// Mapping for job status
const JOB_STATUS_CONFIG = {
    'Not Status Set': '#F7F7F7',
    'Not Actioned': '#B11C03',
    'Notification Raised': '#E86A02',
    'Waiting on Information / Engineering': '#FE9844',
    'Work Order Created': '#DAF2D0',
    'Planning in Progress': '#B5E6A2',
    'Work Planned': '#8ED973',
    'Work Completed': '#4EA42C'
};

// Mapping for job completion rates
const JOB_COMPLETION_RATES = {
    'Not Status Set': '0%',
    'Not Actioned': '0%',
    'Notification Raised': '10%',
    'Waiting on Information / Engineering': '20%',
    'Work Order Created': '30%',
    'Planning in Progress': '40%',
    'Work Planned': '50%',
    'Work Completed': '100%'
};

// Custom sort order for Job Status (index determines priority)
const JOB_STATUS_ORDER = [
    'Not Status Set',
    'Not Actioned',
    'Notification Raised',
    'Waiting on Information / Engineering',
    'Work Order Created',
    'Planning in Progress',
    'Work Planned',
    'Work Completed'
];

const RemedialActions = () => {
    const { defects, setDefects, saveDefectsToExcel = () => { } } = useContext(AssetContext);
    const navigate = useNavigate();

    const [colors, setColors] = useState({});
    const [filters, setFilters] = useState(() => {
        if (USE_LOCAL_STORAGE) {
            const savedFilters = localStorage.getItem('tableFilters');
            return savedFilters ? JSON.parse(savedFilters) : {};
        }
        return {};
    });
    const [filteredDefects, setFilteredDefects] = useState(defects);
    const [activeFilter, setActiveFilter] = useState(null);
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const statusDropdownRef = useRef(null);


    const [isPdfOpen, setIsPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const openPdf = (url) => {
        setPdfUrl(url);
        setIsPdfOpen(true);
    };

    const closePdf = () => {
        setIsPdfOpen(false);
        setPdfUrl(null);
    };

    const textareaRef = useRef(null);

    // Prevent any parent <form> submission from reloading the page
    useEffect(() => {
      const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
    
      // capture=true so we catch submits even if they come from a parent form
      window.addEventListener('submit', onSubmit, true);
      return () => window.removeEventListener('submit', onSubmit, true);
    }, []);

    


    const [columns, setColumns] = useState(() => {
        if (USE_LOCAL_STORAGE) {
            const savedColumns = localStorage.getItem('tableColumns');
            if (savedColumns) {
                return JSON.parse(savedColumns);
            }
        }
        return [
            { field: 'asset_name', label: 'Asset', visible: true, showFilter: true },
            { field: 'defect_id', label: 'Defect', visible: true, showFilter: true },
            { field: 'defect_type', label: 'Defect Type', visible: true, showFilter: true },
            { field: 'risk_rate', label: 'Risk Level', visible: true, showFilter: true },
            { field: 'score', label: 'Risk Score', visible: true, showFilter: false },
            { field: 'severity_level', label: 'Severity Level', visible: true, showFilter: true },
            { field: 'remedial action description', label: 'Remedial Action Description', visible: true, showFilter: true },
            { field: 'Task type', label: 'Task Type', visible: true, showFilter: true },
            { field: 'Cost ($ AUD)', label: 'Cost', visible: true, showFilter: false },
            { field: 'remediation timeframe', label: 'Remediation Timeframe', visible: true, showFilter: true },
            { field: 'repair_ref', label: 'Remedial Procedure', visible: true, showFilter: false },
            { field: 'closure_report', label: 'Closure Report', visible: true, showFilter: false },
            { field: 'Commentary', label: 'Commentary', visible: true, showFilter: true },
            { field: 'Work Order N°/Notification N°', label: 'Work Order N°/Notification N°', visible: true, showFilter: true },
            { field: 'Job Status', label: 'Job Status', visible: true, showFilter: true },
            { field: 'Job Completion Rate', label: 'Job Completion Rate', visible: true, showFilter: false },
        ];
    });

    // Save column state to localStorage when it changes
    useEffect(() => {
        if (!USE_LOCAL_STORAGE) return;
        localStorage.setItem('tableColumns', JSON.stringify(columns));
    }, [columns]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setEditingId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const ColumnVisibilityDropdown = () => (
        <div className="column-visibility-container">
            <button
                type="button"
                className="hide-show-button"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
                <div className="button-content">
                    <h6 style={{ fontSize: '16px' }}>Hide/Show Column</h6>
                    <img
                        src={filterIcon}
                        alt="Toggle columns"
                        className={`dropdown-arrow ${showColumnDropdown ? 'rotated' : ''}`}
                    />
                </div>
            </button>

            {showColumnDropdown && (
                <div className="column-dropdown">
                    <div className="dropdown-content">
                        {columns.map((column, index) => (
                            <div key={index} className="column-option">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={column.visible}
                                        onChange={() => toggleColumnVisibility(index)}
                                        className="checkbox-input"
                                    />
                                    <span className="checkbox-text">{column.label}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            const container = document.querySelector('.column-visibility-container');
            if (container && !container.contains(event.target)) {
                setShowColumnDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleColumnVisibility = (columnIndex) => {
        setColumns(prevColumns => prevColumns.map((column, index) =>
            index === columnIndex ? { ...column, visible: !column.visible } : column
        ));
    };

    const getUniqueValues = (field) => {
        const relevantFilters = Object.fromEntries(
            Object.entries(filters).filter(([key]) => key !== field)
        );

        const filteredForField = defects.filter(defect =>
            Object.entries(relevantFilters).every(([key, values]) => {
                if (!values || values.length === 0) return true;
                const defectValue = String(defect[key] || '').toLowerCase();
                return values.some(value =>
                    String(value).toLowerCase() === defectValue
                );
            })
        );

        return [...new Set(filteredForField.map(defect => defect[field]))].filter(Boolean);
    };

    useEffect(() => {
        const riskColors = {
            'Very High': getCssVariableValue('--risk-color-4'),
            'High': getCssVariableValue('--risk-color-3'),
            'Medium': getCssVariableValue('--risk-color-2'),
            'Low': getCssVariableValue('--risk-color-1'),
        };
        setColors(riskColors);
    }, []);

    useEffect(() => {
        filterData();
    }, [filters, defects]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.filter-dropdown') && !event.target.closest('.filter-icon')) {
                setActiveFilter(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getCssVariableValue = (variableName) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    };

    const goBack = () => {
        navigate('/analytics');
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(value);
    };

    const handleFilterClick = (field) => {
        setActiveFilter(activeFilter === field ? null : field);
    };

    useEffect(() => {
        if (!USE_LOCAL_STORAGE) return;
        localStorage.setItem('tableFilters', JSON.stringify(filters));
    }, [filters]);

    const handleFilterSelect = (field, value) => {
        setFilters(prev => {
            const currentValues = prev[field] || [];
            const valueIndex = currentValues.indexOf(value);

            let newValues;
            if (valueIndex === -1) {
                newValues = [...currentValues, value];
            } else {
                newValues = currentValues.filter((_, index) => index !== valueIndex);
            }

            const newFilters = {
                ...prev,
                [field]: newValues
            };

            // Si el array está vacío, eliminar la propiedad
            if (newFilters[field].length === 0) {
                delete newFilters[field];
            }

            // Guardar en localStorage
            if (USE_LOCAL_STORAGE) {
                localStorage.setItem('tableFilters', JSON.stringify(newFilters));
            }
            return newFilters;
        });
    };

    const clearFilter = (field) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters[field];
            // Guardar en localStorage
            if (USE_LOCAL_STORAGE) {
                localStorage.setItem('tableFilters', JSON.stringify(newFilters));
            }
            return newFilters;
        });
    };

    const filterData = () => {
        const filtered = defects.filter(defect => {
            return Object.entries(filters).every(([key, values]) => {
                if (!values || values.length === 0) return true;
    
                const defectValue = String(defect[key] || '').toLowerCase();
    
                // Special case for numeric filtering (e.g., Cost)
                if (key === 'Cost ($ AUD)') {
                    return values.some(value => parseFloat(defect[key]) === parseFloat(value));
                }
    
                return values.some(value =>
                    String(value).toLowerCase() === defectValue
                );
            });
        });
    
        setFilteredDefects(filtered);
    };

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const sortedDefects = [...filteredDefects].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let valueA = a[sortConfig.key];
        let valueB = b[sortConfig.key];

        // Handle numeric sorting for Cost
        if (sortConfig.key === 'Cost ($ AUD)') {
            valueA = parseFloat(valueA) || 0;
            valueB = parseFloat(valueB) || 0;
        }
        // Handle numeric sorting for Risk Score (1-25)
        else if (sortConfig.key === 'score') {
            valueA = parseFloat(valueA) || 0;
            valueB = parseFloat(valueB) || 0;
        } 
        // Handle custom sorting for Job Status based on workflow order
        else if (sortConfig.key === 'Job Status') {
            const isBlankA = !valueA || valueA === '';
            const isBlankB = !valueB || valueB === '';
            
            // Blanks always go to the end, regardless of sort direction
            if (isBlankA && isBlankB) return 0;
            if (isBlankA) return 1;
            if (isBlankB) return -1;
            
            valueA = JOB_STATUS_ORDER.indexOf(valueA);
            valueB = JOB_STATUS_ORDER.indexOf(valueB);
            // Unknown status goes to end (but before blanks)
            if (valueA === -1) valueA = JOB_STATUS_ORDER.length;
            if (valueB === -1) valueB = JOB_STATUS_ORDER.length;
        }
        // Handle sorting for Job Completion Rate by percentage value
        else if (sortConfig.key === 'Job Completion Rate') {
            const isBlankA = !valueA || valueA === '';
            const isBlankB = !valueB || valueB === '';
            
            // Blanks always go to the end, regardless of sort direction
            if (isBlankA && isBlankB) return 0;
            if (isBlankA) return 1;
            if (isBlankB) return -1;
            
            // Extract numeric percentage from the value (e.g., '10%' -> 10)
            valueA = parseInt(String(valueA).replace('%', ''), 10);
            valueB = parseInt(String(valueB).replace('%', ''), 10);
        }
        else {
            // Alphabetical sorting for text fields
            valueA = String(valueA || '').toLowerCase();
            valueB = String(valueB || '').toLowerCase();
        }

        if (valueA < valueB) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (valueA > valueB) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key !== key) {
                // New column: start with ascending
                return { key, direction: 'ascending' };
            }
            if (prev.direction === 'ascending') {
                // Second click: descending
                return { key, direction: 'descending' };
            }
            // Third click: reset to no sort
            return { key: null, direction: null };
        });
    };

    const FilterDropdown = ({ field }) => {
        const [searchText, setSearchText] = useState('');
        const options = getUniqueValues(field);
        const selectedValues = filters[field] || [];

        return (
            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <button 
                    type="button"
                    className='filter-button' 
                    onClick={(e) => {
                        e.stopPropagation(); // Stop event from bubbling to header
                        handleFilterClick(field);
                    }}
                >
                    <img src={filterIcon} alt="Filter" />
                    {selectedValues.length > 0 && (
                        <span className="filter-count">{selectedValues.length}</span>
                    )}
                </button>

                {activeFilter === field && (
                    <div 
                        className="filter-dropdown"
                        style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 9999,
                            background: 'white',
                            borderRadius: '4px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            minWidth: '200px'
                        }}
                        onClick={(e) => e.stopPropagation()} // Also stop propagation on dropdown
                    >
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="filter-search-input"
                            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                        />
                        <button
                            type="button"
                            className="clear-filter-button" 
                            onClick={(e) => {
                                e.stopPropagation();
                                clearFilter(field);
                            }}
                        >
                            Clear filter
                        </button>
                        <div className='select-options'>
                            {options
                                .filter(option => option.toLowerCase().includes(searchText.toLowerCase()))
                                .map((option, index) => (
                                    <button
                                        type="button"
                                        key={index}
                                        className={`filter-option ${selectedValues.includes(option) ? 'selected-option' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFilterSelect(field, option);
                                        }}
                                    >
                                        <span className="option-text">{option}</span>
                                    </button>
                                ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderHeader = (column) => {
        const isCurrentSortColumn = sortConfig.key === column.field;

        return (
            <th key={column.field} onClick={() => handleSort(column.field)}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                    }}
                >
                    <span>{column.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {column.showFilter && <FilterDropdown field={column.field} />}
                        {isCurrentSortColumn && sortConfig.direction && (
                            <span style={{ fontWeight: 'bold', marginLeft: '5px', color: '#C0C0C0' }}>
                                {sortConfig.direction === 'ascending' ? '▲' : '▼'}
                            </span>
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const handleEdit = async (defectId, field, currentValue) => {
    // Save previous edit if there was one
    if (editingId) {
        await handleSave(editingId.id, editingId.field);
    }
    setEditingId({ id: defectId, field });
    setEditValue(currentValue || '');
};

    const handleStatusClick = (defectId, field, currentEditingId) => {
        // If there is already a cell being edited
        if (currentEditingId) {
            if (currentEditingId.id === defectId && currentEditingId.field === field) {
                setEditingId(null);
                return;
            }
            setEditingId({ id: defectId, field });
            return;
        }
        setEditingId({ id: defectId, field });
    };

    const handleSave = async (defectId, field) => {
        const updatedDefects = defects.map(defect => {
            if (defect.defect_id === defectId) {
                return {
                    ...defect,
                    [field]: editValue,
                };
            }
            return defect;
        });

        if (setDefects) {
            setDefects(updatedDefects);
        }

        // Apply existing filters to the updated set of defects
        const filtered = updatedDefects.filter(defect =>
            Object.entries(filters).every(([key, values]) => {
                if (!values || values.length === 0) return true;
                const defectValue = String(defect[key] || '').toLowerCase();
                return values.some(value =>
                    String(value).toLowerCase() === defectValue
                );
            })
        );
        setFilteredDefects(filtered);

        const success = await saveDefectsToExcel(updatedDefects);
        if (success) {
            console.log('Defects saved successfully');
        } else {
            console.error('Error saving defects');
        }

        setEditingId(null);
    };

    const handleStatusSelect = async (defectId, status) => {
        const updatedDefects = defects.map(defect => {
            if (defect.defect_id === defectId) {
                return {
                    ...defect,
                    'Job Status': status,
                    'Job Completion Rate': JOB_COMPLETION_RATES[status] // Automatically update completion rate
                };
            }
            return defect;
        });

        if (setDefects) {
            setDefects(updatedDefects);
        }

        setFilteredDefects(updatedDefects.filter(defect =>
            Object.entries(filters).every(([key, values]) => {
                if (!values || values.length === 0) return true;
                const defectValue = String(defect[key] || '').toLowerCase();
                return values.some(value =>
                    String(value).toLowerCase() === defectValue
                );
            })
        ));

        const success = await saveDefectsToExcel(updatedDefects);
        if (success) {
            console.log('Status and completion rate updated successfully');
        } else {
            console.error('Error updating status and completion rate');
        }

        setEditingId(null);
    };

    const handleKeyPress = (e, defectId, field) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(defectId, field);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingId(null);
        }
    };

    const StatusDropdown = ({ defectId, currentStatus }) => {
        const handleOptionClick = (e, status) => {
            e.stopPropagation();
            handleStatusSelect(defectId, status);
        };

        return (
            <div className="status-dropdown" onClick={e => e.stopPropagation()}>
                {Object.entries(JOB_STATUS_CONFIG).map(([status, color]) => (
                    <div
                        key={status}
                        className="status-option"
                        style={{ backgroundColor: color }}
                        onClick={(e) => handleOptionClick(e, status)}
                    >
                        {status}
                    </div>
                ))}
            </div>
        );
    };

    const renderCell = (defect, column) => {
        const isEditing = editingId && editingId.id === defect.defect_id && editingId.field === column.field;

        if (column.field === 'Job Status') {
            const currentStatus = defect[column.field];
            const backgroundColor = currentStatus ? JOB_STATUS_CONFIG[currentStatus] : 'transparent';

            return (
                <div className="status-cell-container">
                    <div
                        className={`status-cell ${isEditing ? 'active' : ''}`}
                        style={{ backgroundColor, color: currentStatus ? 'black' : 'gray', }}
                        onClick={(e) => {
                            handleStatusClick(defect.defect_id, column.field, editingId);
                        }}
                    >
                        {currentStatus || 'Select status'}
                    </div>
                    {isEditing && (
                        <StatusDropdown
                            defectId={defect.defect_id}
                            currentStatus={currentStatus}
                        />
                    )}
                </div>
            );
        }

        if (column.field === 'Job Completion Rate') {
            const currentStatus = defect['Job Status'];
            const backgroundColor = currentStatus ? JOB_STATUS_CONFIG[currentStatus] : 'transparent';

            return (
                <div
                    className="status-cell"
                    style={{ backgroundColor, color: currentStatus ? 'black' : 'gray', }}
                >
                    {defect[column.field]}
                </div>
            );
        }

        if (column.field === 'Commentary') {
            return isEditing ? (
                <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleSave(defect.defect_id, column.field)}
                    onKeyDown={(e) => {
                        // Permitir múltiples líneas, solo guardar con Ctrl/Cmd + Enter
                        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                            e.preventDefault();
                            handleSave(defect.defect_id, column.field);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditingId(null);
                        }
                    }}
                    className="editable-cell-textarea"
                    rows={4}
                    placeholder="Enter your comment here..."
                    autoFocus
                />
            ) : (
                <div
                    className="editable-cell commentary"
                    style={{ color: defect[column.field] ? 'black' : 'gray' }}
                    onClick={() => handleEdit(defect.defect_id, column.field, defect[column.field])}
                >
                    {defect[column.field] || 'Click to edit'}
                </div>
            );
        } else if (column.field === 'Work Order N°/Notification N°') {
            return isEditing ? (
                <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleSave(defect.defect_id, column.field)}
                    onKeyDown={(e) => handleKeyPress(e, defect.defect_id, column.field)}
                    className="editable-cell-input"
                />
            ) : (
                <div
                    className="editable-cell"
                    style={{ color: defect[column.field] ? 'black' : 'gray' }}
                    onClick={() => handleEdit(defect.defect_id, column.field, defect[column.field])}
                >
                    {defect[column.field] || 'Click to edit'}
                </div>
            );
        }
        // Handle other special cell types as before
        if (column.field === 'risk_rate') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span
                        style={{
                            backgroundColor: colors[defect.risk_rate],
                            height: '15px',
                            width: '15px',
                            borderRadius: '4px',
                        }}
                    />
                    {defect[column.field]}
                </div>
            );
        }

        if (column.field === 'repair_ref') {
            const value = defect[column.field];
            if (!value) {
                return <span style={{ color: 'gray' }}>No Procedure</span>;
            }
            return (
                <div
                    style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => openPdf(`/remedial_reports/${encodeURIComponent(value)}`)}
                >
                    {value}
                </div>
            );
        }

        if (column.field === 'closure_report') {
            const value = defect[column.field];
            if (!value) {
                return <span style={{ color: 'gray' }}>No Closure</span>;
            }
            return (
                <div
                style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => openPdf(`/closure_reports/${encodeURIComponent(value)}`)}
                >
                {value}
                </div>
            );
        }

        if (column.field === 'defect_id') {
            return (
              <div
                style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate(`/defect-details/${defect.defect_id}`)}
              >
                {defect[column.field]}
              </div>
            );
          }
        
        if (column.field === 'defect_type') {
            return <span>{defect[column.field]}</span>;
        }
        if (column.field === 'score') {
            return <span style={{ display: 'block', textAlign: 'center' }}>{defect[column.field]}</span>;
        }
        if (column.field === 'Cost ($ AUD)') {
            return formatCurrency(defect[column.field]);
        }
        return defect[column.field];
    };
    
    const handleDownloadExcel = async () => {
        // 1. Create a new workbook
        const workbook = new ExcelJS.Workbook();
      
        // 2. Create a worksheet
        const worksheet = workbook.addWorksheet('Defects');
      
        // 3. Define columns (with styling, widths, etc.)
        const visibleColumns = columns.filter(col => col.visible);
        worksheet.columns = visibleColumns.map((col) => ({
          header: col.label,
          key: col.field,
          width: 25,  // or pick a width you like
          style: {
            font: { name: 'Arial', size: 10 }
          }
        }));
      
        // 4. Add the rows
        sortedDefects.forEach(defect => {
          // Build row data: each cell matches a "key" from columns
          const rowData = {};
          visibleColumns.forEach(col => {
            rowData[col.field] = defect[col.field];
          });
          worksheet.addRow(rowData);
        });
      
        // 5. Style the header row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell(cell => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF000000' }
          };
        });
      
        // 6. Write buffer and save
        const buffer = await workbook.xlsx.writeBuffer();
        const file = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file, `Filtered_Defects.xlsx`);
      };
    
    return (
        <div className='cost-analysis-container'>
            <div className='cost-analytics-title'>
                <div>
                    <h2>Analytics</h2>
                    <Link to="/cost-analysis" className='analysis-button'>
                        Cost Analysis
                    </Link>
                    <Link to="/remedial-actions" className='analysis-button-active'>
                        Remedial Actions
                    </Link>
                    <Link to="/graphics" className='analysis-button'>
                        Defect Summary
                    </Link>
                </div>
                <div style={{ display: 'flex', alignContent: 'center' }}>
                <button type="button" className="download-excel-button" onClick={handleDownloadExcel}>
                    Download Defects
                </button>
                    <ColumnVisibilityDropdown />
                    <button type="button" className='back-button' style={{ width: '100px' }} onClick={goBack}>
                        <img src={backIcon} alt="Back" />
                        Analytics
                    </button>
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        {columns
                            .filter(column => column.visible)
                            .map(column => renderHeader(column))}
                    </tr>
                </thead>
                <tbody>
                    {sortedDefects.map((defect, index) => (
                        <tr key={index}>
                            {columns
                                .filter(column => column.visible)
                                .map(column => (
                                    <td key={column.field}>
                                        {renderCell(defect, column)}
                                    </td>
                                ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <PdfViewerModal
                isOpen={isPdfOpen}
                onClose={closePdf}
                fileUrl={pdfUrl}
            />
        </div>
    );
};


export default RemedialActions;